"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { watchConnectProofRecording } from "@/lib/wallet/connect-proof-recording";

async function recordedWallet() {
  return fetch("/api/game/identity/", { signal: AbortSignal.timeout(15_000) })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/**
 * Names the game seat after the extension wallet. Signing in binds it
 * (api/game/identity/solana), but an extension reattaches a returning holder
 * without one, so the game would seat them as "Player 1/2": when the game
 * does not already know this wallet, the holder signs in once more.
 */
export function useClaimGameIdentity(
  solanaAddress: string | null,
  proveWallet: (() => Promise<boolean>) | undefined
) {
  const [binding, setBinding] = useState({
    solanaAddress,
    proveWallet,
    generation: 0,
  });
  if (
    binding.solanaAddress !== solanaAddress ||
    binding.proveWallet !== proveWallet
  )
    setBinding({
      solanaAddress,
      proveWallet,
      generation: binding.generation + 1,
    });
  const active = useRef({ binding, mounted: false });
  useLayoutEffect(() => {
    const committed = { binding, mounted: true };
    active.current = committed;
    return () => {
      committed.mounted = false;
    };
  }, [binding]);
  return useCallback(async () => {
    const expected = active.current;
    const current = () =>
      expected.mounted &&
      active.current === expected &&
      expected.binding === binding &&
      binding.solanaAddress === solanaAddress &&
      binding.proveWallet === proveWallet;
    if (!solanaAddress || !current()) return false;
    // Observe before the read: a write may start and settle during that read.
    const recording = watchConnectProofRecording(solanaAddress);
    try {
      const known = await recordedWallet();
      if (!current()) return false;
      if (known?.wallet === solanaAddress) return true;
      const pending = recording.pending();
      if (pending) {
        await pending;
        if (!current()) return false;
        // A successful local write is not authority; re-read the server proof.
        const recorded = await recordedWallet();
        return current() && recorded?.wallet === solanaAddress;
      }
      const proven = (await proveWallet?.()) ?? false;
      return current() && proven;
    } finally {
      recording.release();
    }
  }, [solanaAddress, proveWallet, binding]);
}
